/**
 * Candor-React — Dev / QA / Prod host resolution.
 *
 * Hosts live in candor_host_matrix.json (config). Build-time env overrides:
 *   REACT_APP_API_BASE_URL, REACT_APP_FRONTEND_ORIGIN, REACT_APP_SSO_REDIRECT_URI,
 *   REACT_APP_SSO_KNOWN_API_HOSTS, REACT_APP_SSO_BLOCKED_PROVIDERS,
 *   REACT_APP_*_LLM_API_URL / REACT_APP_*_FRONTEND_ORIGIN (optional per-slot).
 *
 * @see docs/CANDOR_ENV_DEV_QA_PROD.md
 */
import hostMatrix from './candor_host_matrix.json';

function trimSlash(url) {
  return String(url || '')
    .trim()
    .replace(/\/$/, '');
}

function envStr(key) {
  const raw = process.env[key];
  if (raw === undefined || raw === null) return '';
  return String(raw).trim();
}

function parseCsv(raw) {
  return String(raw || '')
    .split(/[,;]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function hostnameOf(url) {
  try {
    const u = new URL(String(url || '').includes('://') ? url : `https://${url}`);
    return (u.hostname || '').toLowerCase();
  } catch (_) {
    return '';
  }
}

function matrixRow(name) {
  const rows = hostMatrix.environments || {};
  return rows[name] || rows.development || {};
}

/**
 * Check for runtime environment override on cantest/dev/qa hosts.
 * Checks URL search params (?env=qa / ?env=dev) and storage.
 * @returns {'qa'|'dev_server'|''}
 */
export function getRuntimeEnvironmentOverride() {
  if (typeof window === 'undefined') return '';
  try {
    const hostname = (window.location.hostname || '').toLowerCase();
    // Production website never follows saved/query QA-Dev targets (see apiBase.readRuntimeEnv).
    const prod = matrixRow('production');
    const prodHosts = new Set();
    [prod.frontend_origin, prod.dashboard_url].forEach((u) => {
      try {
        const h = new URL(u).hostname.toLowerCase();
        if (!h) return;
        prodHosts.add(h);
        prodHosts.add(h.startsWith('www.') ? h.slice(4) : `www.${h}`);
      } catch (_) {}
    });
    if (prodHosts.has(hostname)) return '';
    if (hostname.includes('ultradocuments')) return 'qa';
    if (hostname.includes('candordev')) return 'dev_server';

    const params = new URLSearchParams(window.location.search);
    const p = (params.get('env') || params.get('api') || params.get('target') || '').toLowerCase();
    if (params.has('qa') || p === 'qa' || p === 'staging') {
      sessionStorage.setItem('candor_target_env', 'qa');
      return 'qa';
    }
    if (params.has('dev') || p === 'dev' || p === 'dev_server' || p === 'devserver') {
      sessionStorage.setItem('candor_target_env', 'dev_server');
      return 'dev_server';
    }

    const ref = (document.referrer || '').toLowerCase();
    if (ref.includes('ultradocuments.com')) return 'qa';
    if (ref.includes('candordev.candorsys.com')) return 'dev_server';

    const stored = (
      sessionStorage.getItem('candor_target_env') ||
      localStorage.getItem('candor_target_env') ||
      ''
    ).toLowerCase();
    if (stored === 'qa') return 'qa';
    if (stored === 'dev_server' || stored === 'dev') return 'dev_server';
  } catch (_) {}
  return '';
}

/**
 * Normalize REACT_APP_ENVIRONMENT with runtime override support.
 * @returns {'development'|'dev_server'|'qa'|'production'}
 */
export function resolveAppEnvironment() {
  const runtime = getRuntimeEnvironmentOverride();
  if (runtime) return runtime;
  const raw = envStr('REACT_APP_ENVIRONMENT').toLowerCase();
  if (raw === 'qa' || raw === 'staging' || raw === 'stage') return 'qa';
  if (raw === 'production' || raw === 'prod') return 'production';
  if (raw === 'dev_server' || raw === 'devserver') return 'dev_server';
  if (raw === 'development' || raw === 'dev' || raw === 'local') return 'development';
  if (process.env.NODE_ENV === 'production') return 'production';
  // Active package target is production. Local laptop still overrides via REACT_APP_ENVIRONMENT=development.
  return 'production';
}

export function isQaEnv() {
  return resolveAppEnvironment() === 'qa';
}

export function isProdEnv() {
  return resolveAppEnvironment() === 'production';
}

export function isDevEnv() {
  return resolveAppEnvironment() === 'development';
}

function slotUrl(envName, field, envOverrideKey) {
  const override = envStr(envOverrideKey);
  if (override) return trimSlash(override);
  return trimSlash(matrixRow(envName)[field] || '');
}

/** Slot constants — matrix + optional per-slot env overrides (no inline host strings). */
export const DEV_LLM_API_URL = slotUrl('development', 'llm_api_url', 'REACT_APP_DEV_LLM_API_URL');
export const DEV_SERVER_LLM_API_URL = slotUrl(
  'dev_server',
  'llm_api_url',
  'REACT_APP_DEV_SERVER_LLM_API_URL'
);
export const QA_LLM_API_URL = slotUrl('qa', 'llm_api_url', 'REACT_APP_QA_LLM_API_URL');
export const PROD_LLM_API_URL = slotUrl('production', 'llm_api_url', 'REACT_APP_PROD_LLM_API_URL');

export const DEV_FRONTEND_ORIGIN = slotUrl(
  'development',
  'frontend_origin',
  'REACT_APP_DEV_FRONTEND_ORIGIN'
);
export const DEV_SERVER_FRONTEND_ORIGIN = slotUrl(
  'dev_server',
  'frontend_origin',
  'REACT_APP_DEV_SERVER_FRONTEND_ORIGIN'
);
export const QA_FRONTEND_ORIGIN = slotUrl('qa', 'frontend_origin', 'REACT_APP_QA_FRONTEND_ORIGIN');
export const PROD_FRONTEND_ORIGIN = slotUrl(
  'production',
  'frontend_origin',
  'REACT_APP_PROD_FRONTEND_ORIGIN'
);

const CALLBACK_PATH = (() => {
  const fromEnv = envStr('REACT_APP_SSO_AUTH_CALLBACK_PATH');
  if (fromEnv) return fromEnv.startsWith('/') ? fromEnv : `/${fromEnv}`;
  const fromMatrix = String(hostMatrix.sso_auth_callback_path || '').trim();
  if (!fromMatrix) {
    throw new Error('candor_host_matrix.json sso_auth_callback_path is required');
  }
  return fromMatrix.startsWith('/') ? fromMatrix : `/${fromMatrix}`;
})();

export const DEV_SSO_REDIRECT_URI = `${DEV_FRONTEND_ORIGIN}${CALLBACK_PATH}`;
export const DEV_SERVER_SSO_REDIRECT_URI = `${DEV_SERVER_FRONTEND_ORIGIN}${CALLBACK_PATH}`;
export const QA_SSO_REDIRECT_URI = `${QA_FRONTEND_ORIGIN}${CALLBACK_PATH}`;
export const PROD_SSO_REDIRECT_URI = `${PROD_FRONTEND_ORIGIN}${CALLBACK_PATH}`;

export function ssoAuthCallbackPath() {
  return CALLBACK_PATH.startsWith('/') ? CALLBACK_PATH : `/${CALLBACK_PATH}`;
}

/**
 * LLM / API hostnames that must never be used as SPA OAuth return targets.
 * Env CSV wins; else matrix known_api_hosts + hostnames derived from llm slots.
 */
export function knownApiHostnames() {
  const fromEnv = parseCsv(envStr('REACT_APP_SSO_KNOWN_API_HOSTS'));
  const hosts = new Set(fromEnv);
  if (!fromEnv.length) {
    for (const h of hostMatrix.known_api_hosts || []) {
      if (h) hosts.add(String(h).toLowerCase());
    }
  }
  for (const url of [DEV_LLM_API_URL, DEV_SERVER_LLM_API_URL, QA_LLM_API_URL, PROD_LLM_API_URL]) {
    const h = hostnameOf(url);
    if (!h) continue;
    hosts.add(h);
    if (h.startsWith('www.')) hosts.add(h.slice(4));
    else hosts.add(`www.${h}`);
  }
  return hosts;
}

export function isKnownApiHostname(hostname) {
  const h = String(hostname || '')
    .trim()
    .toLowerCase();
  return Boolean(h) && knownApiHostnames().has(h);
}

/** Providers hidden on Web login (matrix + REACT_APP_SSO_BLOCKED_PROVIDERS). */
export function blockedSsoProviders() {
  const fromEnv = parseCsv(envStr('REACT_APP_SSO_BLOCKED_PROVIDERS'));
  if (fromEnv.length) return fromEnv;
  return (hostMatrix.blocked_public_providers || []).map((p) => String(p).toLowerCase());
}

export function isBlockedSsoProvider(providerId) {
  const id = String(providerId || '')
    .trim()
    .toLowerCase();
  return Boolean(id) && blockedSsoProviders().includes(id);
}

/**
 * Default LLM API base when REACT_APP_API_BASE_URL is unset.
 * DEV builds: empty string → same-origin + CRA proxy (preferred).
 */
export function defaultLlmApiBaseUrl() {
  const env = resolveAppEnvironment();
  if (env === 'qa') return QA_LLM_API_URL;
  if (env === 'production') return PROD_LLM_API_URL;
  if (env === 'dev_server') return DEV_SERVER_LLM_API_URL;
  return '';
}

/** Absolute default for tools / non-proxy clients (always a host when matrix configured). */
export function defaultLlmApiBaseUrlAbsolute() {
  const env = resolveAppEnvironment();
  if (env === 'qa') return QA_LLM_API_URL;
  if (env === 'production') return PROD_LLM_API_URL;
  if (env === 'dev_server') return DEV_SERVER_LLM_API_URL;
  return DEV_LLM_API_URL;
}

export function defaultFrontendOrigin() {
  const fromEnv = envStr('REACT_APP_FRONTEND_ORIGIN');
  if (fromEnv) return trimSlash(fromEnv);
  const env = resolveAppEnvironment();
  if (env === 'qa') return QA_FRONTEND_ORIGIN;
  if (env === 'production') return PROD_FRONTEND_ORIGIN;
  if (env === 'dev_server') return DEV_SERVER_FRONTEND_ORIGIN;
  return DEV_FRONTEND_ORIGIN;
}

export function defaultSsoRedirectUri() {
  const fromEnv = envStr('REACT_APP_SSO_REDIRECT_URI');
  if (fromEnv) return fromEnv;
  const env = resolveAppEnvironment();
  if (env === 'qa') return QA_SSO_REDIRECT_URI;
  if (env === 'production') return PROD_SSO_REDIRECT_URI;
  if (env === 'dev_server') return DEV_SERVER_SSO_REDIRECT_URI;
  return DEV_SSO_REDIRECT_URI;
}

// ***** Code Start BH15092026_1445 - Canonical SPA origin pin (prod must not use cantest)
function normalizeSpaHostname(hostname) {
  return String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
}

/** True when origins are the same SPA site (www ↔ apex treated as equal). */
export function sameSpaSite(originA, originB) {
  try {
    const a = new URL(String(originA || ''));
    const b = new URL(String(originB || ''));
    return normalizeSpaHostname(a.hostname) === normalizeSpaHostname(b.hostname);
  } catch (_) {
    return false;
  }
}

/**
 * Canonical SPA origin for the active package env (matrix / REACT_APP_FRONTEND_ORIGIN).
 * Production → candorsys.com; never cantest for production packages.
 */
export function canonicalSpaOrigin() {
  return defaultFrontendOrigin() || productionFrontendOrigin();
}

/** Opt-out for rare local debugging of a production-baked bundle. */
export function allowNonCanonicalSpaHost() {
  const raw = envStr('REACT_APP_ALLOW_NONCANONICAL_SPA').toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

/**
 * Production (and packaged QA/dev_server) must run on their matrix SPA host.
 * Returns an absolute URL to bounce to, or '' when already canonical / allowed.
 */
export function canonicalSpaBounceUrl(currentHref) {
  if (typeof window === 'undefined') return '';
  if (allowNonCanonicalSpaHost()) return '';
  const env = resolveAppEnvironment();
  // Laptop local development stays on whatever origin is open.
  if (env === 'development') return '';

  const canonical = canonicalSpaOrigin();
  if (!canonical) return '';
  try {
    const here = new URL(currentHref || window.location.href);
    // Do not bounce mid OAuth callback — sessionStorage state is origin-bound.
    const cb = (ssoAuthCallbackPath() || '/auth/callback').replace(/\/+$/, '') || '/auth/callback';
    const path = (here.pathname || '/').replace(/\/+$/, '') || '/';
    if (
      path === cb &&
      (here.searchParams.has('code') ||
        here.searchParams.has('state') ||
        here.searchParams.has('error'))
    ) {
      return '';
    }
    if (isKnownApiHostname(here.hostname)) {
      // API host pages must not host the SPA auth UI — bounce to canonical SPA.
      const keep = `${here.pathname || '/'}${here.search || ''}${here.hash || ''}`;
      return `${canonical.replace(/\/$/, '')}${keep.startsWith('/') ? keep : `/${keep}`}`;
    }
    if (sameSpaSite(here.origin, canonical)) return '';
    // Production package opened on cantest / wrong Dev host → force candorsys.com.
    if (env === 'production' || env === 'qa' || env === 'dev_server') {
      const keep = `${here.pathname || '/'}${here.search || ''}${here.hash || ''}`;
      return `${canonical.replace(/\/$/, '')}${keep.startsWith('/') ? keep : `/${keep}`}`;
    }
  } catch (_) {
    return '';
  }
  return '';
}

/**
 * If the production (or packaged host) SPA is on the wrong origin, navigate there.
 * @returns {boolean} true when a bounce was started (caller must stop further work)
 */
export function ensureCanonicalSpaHost() {
  if (typeof window === 'undefined') return false;
  const target = canonicalSpaBounceUrl(window.location.href);
  if (!target) return false;
  try {
    if (sameSpaSite(window.location.href, target)) return false;
  } catch (_) {}
  window.location.replace(target);
  return true;
}
// ***** Code End BH15092026_1445

/**
 * Whether SPA must validate Agent OAuth `state` on /auth/callback.
 * Env REACT_APP_SSO_REQUIRE_OAUTH_STATE wins; else matrix per environment.
 */
export function ssoRequireOauthState() {
  const raw = envStr('REACT_APP_SSO_REQUIRE_OAUTH_STATE').toLowerCase();
  if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on') return true;
  if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'off') return false;
  const env = resolveAppEnvironment();
  const map = hostMatrix.sso_require_oauth_state || {};
  return Boolean(map[env]);
}

/** sessionStorage key for Agent OAuth state (matrix / env — no inline literals in callers). */
export function ssoOauthStateStorageKey() {
  const fromEnv = envStr('REACT_APP_SSO_OAUTH_STATE_KEY');
  if (fromEnv) return fromEnv.slice(0, 64);
  const fromMatrix = String(hostMatrix.sso_oauth_state_storage_key || '').trim();
  if (!fromMatrix) {
    throw new Error('candor_host_matrix.json sso_oauth_state_storage_key is required');
  }
  return fromMatrix.slice(0, 64);
}

/** Max age for an in-flight browser SSO start (ms). */
export function ssoAuthMaxAgeMs() {
  const fromEnv = Number(envStr('REACT_APP_SSO_AUTH_MAX_AGE_MS'));
  if (Number.isFinite(fromEnv) && fromEnv >= 60_000 && fromEnv <= 3_600_000) {
    return Math.floor(fromEnv);
  }
  const fromMatrix = Number(hostMatrix.sso_auth_max_age_ms);
  if (Number.isFinite(fromMatrix) && fromMatrix >= 60_000) return Math.floor(fromMatrix);
  throw new Error('candor_host_matrix.json sso_auth_max_age_ms is required');
}

/**
 * Web product session mode: cookie (JWT HttpOnly) | hybrid (cookie + legacy api_key).
 * Env REACT_APP_SSO_WEB_SESSION_MODE wins; else matrix per environment.
 */
export function ssoWebSessionMode() {
  const raw = envStr('REACT_APP_SSO_WEB_SESSION_MODE').toLowerCase();
  if (raw === 'cookie' || raw === 'jwt' || raw === 'hybrid' || raw === 'api_key') {
    return raw === 'jwt' ? 'cookie' : raw;
  }
  const env = resolveAppEnvironment();
  const map = hostMatrix.sso_web_session_mode || {};
  if (!Object.prototype.hasOwnProperty.call(map, env)) {
    throw new Error(`candor_host_matrix.json sso_web_session_mode.${env} is required`);
  }
  const mode = String(map[env] || '').toLowerCase();
  if (mode === 'hybrid' || mode === 'api_key' || mode === 'cookie') return mode;
  throw new Error(`candor_host_matrix.json sso_web_session_mode.${env} must be cookie|hybrid|api_key`);
}

/** True when SPA must not keep or send X-API-Key (V3 cookie JWT standard). */
export function ssoPreferCookieSession() {
  return ssoWebSessionMode() === 'cookie';
}

/**
 * Dev-only escape hatch for legacy api_key in the browser.
 * QA/Prod default false via matrix; override with REACT_APP_SSO_ALLOW_LEGACY_API_KEY_CLIENT.
 */
export function ssoAllowLegacyApiKeyClient() {
  const raw = envStr('REACT_APP_SSO_ALLOW_LEGACY_API_KEY_CLIENT').toLowerCase();
  if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on') return true;
  if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'off') return false;
  if (ssoPreferCookieSession()) return false;
  const env = resolveAppEnvironment();
  const map = hostMatrix.sso_allow_legacy_api_key_client || {};
  return Boolean(map[env]);
}

/** Web SSO client_app_id — matrix / REACT_APP_SSO_WEB_CLIENT_APP_ID (no hardcode in call sites). */
export function ssoWebClientAppId() {
  const fromEnv = envStr('REACT_APP_SSO_WEB_CLIENT_APP_ID');
  if (fromEnv) return fromEnv.slice(0, 64);
  const fromMatrix = String(hostMatrix.sso_web_client_app_id || '').trim();
  if (!fromMatrix) {
    throw new Error('candor_host_matrix.json sso_web_client_app_id is required');
  }
  return fromMatrix.slice(0, 64);
}

function withSlash(path) {
  const p = String(path || '').trim();
  if (!p) return p;
  return p.startsWith('/') ? p : `/${p}`;
}

/** Agent-side OAuth callback path (API host), not SPA /auth/callback. */
export function ssoAgentOauthCallbackPath() {
  const fromEnv = envStr('REACT_APP_SSO_AGENT_OAUTH_CALLBACK_PATH');
  if (fromEnv) return withSlash(fromEnv);
  const fromMatrix = String(hostMatrix.sso_agent_oauth_callback_path || '').trim();
  if (!fromMatrix) {
    throw new Error('candor_host_matrix.json sso_agent_oauth_callback_path is required');
  }
  return withSlash(fromMatrix);
}

/** Named SSO API path from matrix (providers, login_start, …). */
export function ssoApiPath(key) {
  const paths = hostMatrix.sso_api_paths || {};
  const fromEnv = envStr(`REACT_APP_SSO_API_PATH_${String(key || '').toUpperCase()}`);
  if (fromEnv) return withSlash(fromEnv);
  const raw = String(paths[key] || '').trim();
  if (!raw) {
    throw new Error(`candor_host_matrix.json sso_api_paths.${key} is required`);
  }
  return withSlash(raw);
}

/** Named legacy /api/v1/auth/* path from matrix (register, OTP, …). */
export function legacyApiPath(key) {
  const paths = hostMatrix.legacy_api_paths || {};
  const fromEnv = envStr(`REACT_APP_LEGACY_API_PATH_${String(key || '').toUpperCase()}`);
  if (fromEnv) return withSlash(fromEnv);
  const raw = String(paths[key] || '').trim();
  if (!raw) {
    throw new Error(`candor_host_matrix.json legacy_api_paths.${key} is required`);
  }
  return withSlash(raw);
}

/** Marketing / prod SPA origin used for logo CDN and support links when env unset. */
export function productionFrontendOrigin() {
  return PROD_FRONTEND_ORIGIN;
}

export function defaultSupportEmail() {
  const fromEnv = envStr('REACT_APP_SUPPORT_EMAIL');
  if (fromEnv) return fromEnv;
  return String(hostMatrix.support_email || '').trim();
}

/** Absolute logo URL: env → production origin + matrix path. */
export function defaultLogoCdnUrl() {
  const fromEnv = envStr('REACT_APP_LOGO_CDN_URL');
  if (fromEnv) return fromEnv;
  const path = String(hostMatrix.logo_cdn_path || '/candorlogo.png');
  const origin = productionFrontendOrigin();
  if (!origin) return '';
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Candor Web IDE entry URL */
export const CANDOR_WEB_IDE_URL = envStr('REACT_APP_WEB_IDE_URL') || 'https://candorweb.candorsys.com/candor-web';

export default {
  DEV_LLM_API_URL,
  DEV_SERVER_LLM_API_URL,
  QA_LLM_API_URL,
  PROD_LLM_API_URL,
  DEV_FRONTEND_ORIGIN,
  DEV_SERVER_FRONTEND_ORIGIN,
  QA_FRONTEND_ORIGIN,
  PROD_FRONTEND_ORIGIN,
  CANDOR_WEB_IDE_URL,
  resolveAppEnvironment,
  defaultLlmApiBaseUrl,
  defaultLlmApiBaseUrlAbsolute,
  knownApiHostnames,
  blockedSsoProviders,
  defaultFrontendOrigin,
  defaultSsoRedirectUri,
  sameSpaSite,
  canonicalSpaOrigin,
  allowNonCanonicalSpaHost,
  canonicalSpaBounceUrl,
  ensureCanonicalSpaHost,
  ssoRequireOauthState,
  ssoOauthStateStorageKey,
  ssoAuthMaxAgeMs,
  ssoWebSessionMode,
  ssoPreferCookieSession,
  ssoAllowLegacyApiKeyClient,
  defaultSupportEmail,
  defaultLogoCdnUrl,
};
